import axios from 'axios';
import { config } from '@/config';
import {
  getTenantHostHeaders,
  isApplicationHost,
  isLocalHost,
  resolveTenantByHost,
  setStoredTenantId
} from '@/feature/auth/utils/tenantStorage';
import { adminRoutes } from './shared/adminRoutes';
import { authRoutes } from './shared/authRoutes';
import { dashboardRoutes } from './shared/dashboardRoutes';
import { hsdeskRoutes } from './tenants/hsdesk';
import { serviceBusinessTenantRoutes } from './tenants/serviceBusinessTenant';
import {
  setRouteContext,
  type AppRouteContext,
  type AppRouteScope
} from './routeScope';
import type { AppRouteChild, AppRouteConfig } from './types';

interface RoutePreset {
  routes: AppRouteConfig[];
  includeSharedRoutes?: boolean;
}

interface BackendRoutesResult {
  routes: AppRouteConfig[];
  scope?: AppRouteScope;
  tenantId?: string;
  publicProfileFriendlyUrl?: string;
}

const sharedRoutes: AppRouteConfig[] = [
  ...authRoutes,
  ...dashboardRoutes,
  ...adminRoutes
];
const publicLegalChildren = (authRoutes[0]?.children ?? []).filter((child) =>
  [
    'TermsPage',
    'PrivacyPage',
    'CookiesPage',
    'ActivateAccountPage',
    'CompanyOwnerOnboardingPage'
  ].includes(child.element)
);

const defaultAppRoutePreset: RoutePreset = {
  routes: hsdeskRoutes,
  includeSharedRoutes: true
};

const tenantDomainRouteElementNames: Record<string, string> = {
  IdCard: 'PublicWebsitePage',
  ClientDynamicPage: 'PublicWebsitePage'
};

const appShellElementNames = new Set([
  'LandingPage',
  'AdminOverview',
  'AdminRolePermissions',
  'AdminTenantSettings',
  'AdminTenantPackages',
  'AdminTenantPackageDetails',
  'AdminCompanyDetails',
  'AdminCompanyOnboarding',
  'AdminWebsiteTemplates',
  'AdminSmsDashboard',
  'AdminHangfire',
  'AdminHelpdesk',
  'AdminUserLogs'
]);

const tenantPublicElementNames = new Set([
  'ServiceBusinessPublicPage',
  'PublicWebsitePage',
  'IdCard',
  'ClientDynamicPage'
]);

const serviceBusinessPublicElementNames = new Set([
  'ServiceBusinessPublicPage',
  'PublicWebsitePage',
  'IdCard'
]);

const tenantBackofficeElementNames = new Set([
  'ServiceBusinessBackofficePage',
  'WebsiteBuilderAdminPage',
  'WebsiteBuilderEditorPage',
  'WebsiteBuilderFormsPage',
  'WebsiteBuilderMenusPage',
  'WebsiteBuilderPagesEntryPage',
  'WebsiteBuilderPreviewEntryPage',
  'WebsiteBuilderPreviewPage'
]);

const tenantBackofficePathNames = new Set(['/admin', '/backoffice', '/panel']);

const tenantManagedRoutePathNames = new Set([
  '/login',
  '/create-account',
  '/activate-account',
  '/onboarding',
  '/set-password',
  '/404',
  '/admin',
  '/admin/website/preview',
  '/admin/website-preview',
  '/admin/website/pages/:pageId/preview',
  '/order-status/:token',
  '/order/:orderId'
]);

function normalizeHost(host: string) {
  return host
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/\.$/, '');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readStringValue(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
}

function readScopeValue(value?: string) {
  if (value === 'tenant' || value === 'app') {
    return value;
  }

  return undefined;
}

function readHeaderValue(
  headers: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = headers[key.toLowerCase()] ?? headers[key];

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
}

function readRoutesResponse(
  data: unknown,
  headers: Record<string, unknown>
): BackendRoutesResult | null {
  if (Array.isArray(data)) {
    return {
      routes: data,
      tenantId: readHeaderValue(headers, ['x-tenant-id', 'tenant-id']),
      publicProfileFriendlyUrl: readHeaderValue(headers, [
        'x-public-profile-friendly-url',
        'public-profile-friendly-url'
      ]),
      scope: readScopeValue(
        readHeaderValue(headers, ['x-route-scope', 'route-scope'])
      )
    };
  }

  if (!isRecord(data)) {
    return null;
  }

  const routes = data.routes ?? data.items ?? data.data;

  if (!Array.isArray(routes)) {
    return null;
  }

  return {
    routes,
    tenantId:
      readStringValue(data, ['tenantId', 'id']) ??
      readHeaderValue(headers, ['x-tenant-id', 'tenant-id']),
    publicProfileFriendlyUrl:
      readStringValue(data, [
        'publicProfileFriendlyUrl',
        'friendlyUrl',
        'profileFriendlyUrl'
      ]) ??
      readHeaderValue(headers, [
        'x-public-profile-friendly-url',
        'public-profile-friendly-url'
      ]),
    scope:
      readScopeValue(readStringValue(data, ['scope', 'routeScope'])) ??
      readScopeValue(readHeaderValue(headers, ['x-route-scope', 'route-scope']))
  };
}

async function fetchRoutesFromBackend(
  host: string
): Promise<BackendRoutesResult | null> {
  try {
    const response = await axios.get<unknown>(
      `${config.apiUrl}/api/v1/app-routes/current`,
      {
        params: { host },
        headers: getTenantHostHeaders(),
        timeout: 1500
      }
    );

    return readRoutesResponse(response.data, response.headers);
  } catch {
    return null;
  }
}

async function resolveTenantContext(host: string) {
  try {
    return await resolveTenantByHost(host);
  } catch {
    return null;
  }
}

function cloneChildRoute(child: AppRouteChild): AppRouteChild {
  return { ...child };
}

function cloneRoute(route: AppRouteConfig): AppRouteConfig {
  return {
    ...route,
    children: route.children?.map(cloneChildRoute)
  };
}

function cloneRoutes(routes: AppRouteConfig[]) {
  return routes.map(cloneRoute);
}

function childKey(child: AppRouteChild) {
  return `${child.index ? 'index' : (child.path ?? '')}:${child.element}`;
}

function mergeRouteChildren(
  currentChildren: AppRouteChild[] | undefined,
  requiredChildren: AppRouteChild[]
) {
  const existingKeys = new Set((currentChildren ?? []).map(childKey));
  const missingChildren = requiredChildren.filter(
    (child) => !existingKeys.has(childKey(child))
  );

  return [...(currentChildren ?? []), ...missingChildren.map(cloneChildRoute)];
}

function routeHasChildElement(
  route: AppRouteConfig,
  elementNames: Set<string>
) {
  return (route.children ?? []).some((child) =>
    elementNames.has(child.element)
  );
}

function routesHaveElement(
  routes: AppRouteConfig[],
  elementNames: Set<string>
) {
  return routes.some(
    (route) =>
      (route.element ? elementNames.has(route.element) : false) ||
      routeHasChildElement(route, elementNames)
  );
}

function isTenantBackofficeRoute(route: AppRouteConfig) {
  return (
    tenantBackofficePathNames.has(route.path) ||
    routeHasChildElement(route, tenantBackofficeElementNames)
  );
}

function normalizeRouteElements(
  routes: AppRouteConfig[],
  elementNames: Record<string, string>
): AppRouteConfig[] {
  return routes.map((route) => ({
    ...route,
    element: route.element
      ? (elementNames[route.element] ?? route.element)
      : route.element,
    children: route.children?.map((child) => ({
      ...child,
      element: elementNames[child.element] ?? child.element
    }))
  }));
}

function normalizeTenantDomainRouteElements(routes: AppRouteConfig[]) {
  return normalizeRouteElements(routes, tenantDomainRouteElementNames);
}

function getLocalServiceBusinessRoutes() {
  return hsdeskRoutes
    .filter((route) => isServiceBusinessRoute(route))
    .map(cloneRoute);
}

function isServiceBusinessRoute(route: AppRouteConfig) {
  return route.children?.some((child) =>
    [
      'ServiceBusinessPublicPage',
      'ServiceBusinessBackofficePage',
      'PublicWebsitePage',
      'WebsiteBuilderAdminPage',
      'WebsiteBuilderEditorPage',
      'WebsiteBuilderFormsPage',
      'WebsiteBuilderMenusPage',
      'WebsiteBuilderPagesEntryPage',
      'WebsiteBuilderPreviewEntryPage',
      'WebsiteBuilderPreviewPage',
      'PublicOrderViewPage'
    ].includes(child.element)
  );
}

function insertRoutesBeforeAuthFallback(
  routes: AppRouteConfig[],
  routesToInsert: AppRouteConfig[]
) {
  const fallbackIndex = routes.findIndex(
    (route) =>
      route.path === '/' && route.children?.some((child) => child.path === '*')
  );

  if (fallbackIndex === -1) {
    return [...routes, ...routesToInsert];
  }

  return [
    ...routes.slice(0, fallbackIndex),
    ...routesToInsert,
    ...routes.slice(fallbackIndex)
  ];
}

function ensureServiceBusinessRoutes(routes: AppRouteConfig[]) {
  const localRoutes = getLocalServiceBusinessRoutes();
  const routesWithoutLocalServiceBusiness = routes.filter(
    (route) => !isServiceBusinessRoute(route)
  );

  return insertRoutesBeforeAuthFallback(
    routesWithoutLocalServiceBusiness,
    localRoutes
  );
}

function removeDeprecatedDashboardRoutes(
  routes: AppRouteConfig[]
): AppRouteConfig[] {
  return routes
    .filter((route) => route.path !== '/dashboard/order')
    .map((route) => {
      if (route.path !== '/dashboard') {
        return route;
      }

      return {
        ...route,
        children: route.children?.filter((child) => child.path !== 'settings')
      };
    });
}

function removeLegacyDemoRoutes(routes: AppRouteConfig[]) {
  return routes.filter(
    (route) => route.path !== '/demo' && !route.path.startsWith('/demo/')
  );
}

function removeBackofficeLayoutConfigRoutes(
  routes: AppRouteConfig[]
): AppRouteConfig[] {
  return routes
    .filter((route) => route.path !== '/layout-config')
    .map((route) => ({
      ...route,
      children: route.children?.filter(
        (child) => child.path !== 'layout-config'
      )
    }));
}

function ensureAdminManagementRoutes(
  routes: AppRouteConfig[]
): AppRouteConfig[] {
  const localAdminRoute = adminRoutes.find((route) => route.path === '/admin');
  const requiredAdminElements = new Set([
    'AdminOverview',
    'AdminRolePermissions',
    'AdminTenantSettings',
    'AdminTenantPackages',
    'AdminTenantPackageDetails',
    'AdminCompanyDetails',
    'AdminCompanyUserProfile',
    'AdminWebsiteTemplates',
    'AdminSmsDashboard',
    'AdminHangfire',
    'AdminHelpdesk',
    'AdminUserLogs'
  ]);
  const localAdminChildren = (localAdminRoute?.children ?? []).filter((child) =>
    requiredAdminElements.has(child.element)
  );

  if (!localAdminRoute || localAdminChildren.length === 0) {
    return routes;
  }

  const hasAdminRoute = routes.some((route) => route.path === '/admin');

  if (!hasAdminRoute) {
    return [...routes, localAdminRoute];
  }

  return routes.map((route) => {
    if (route.path !== '/admin') {
      return route;
    }

    const children = route.children ?? [];
    const missingChildren = localAdminChildren.filter(
      (localChild) =>
        !children.some(
          (child) =>
            child.element === localChild.element ||
            child.path === localChild.path
        )
    );

    return {
      ...route,
      children: [...children, ...missingChildren]
    };
  });
}

function prepareAppRoutesFromBackend(routes: AppRouteConfig[]) {
  const routesWithoutDeprecatedDashboard = removeBackofficeLayoutConfigRoutes(
    removeDeprecatedDashboardRoutes(
      removeLegacyPublicProfileChildren(removeLegacyDemoRoutes(routes))
    )
  );
  const routesWithServiceBusiness = ensureServiceBusinessRoutes(
    routesWithoutDeprecatedDashboard
  );

  return ensurePublicLegalRoutes(
    ensureAdminManagementRoutes(routesWithServiceBusiness)
  );
}

function ensurePublicLegalRoutes(routes: AppRouteConfig[]): AppRouteConfig[] {
  if (publicLegalChildren.length === 0) {
    return routes;
  }

  const existingLegalPaths = new Set(
    routes
      .filter((route) => route.path === '/')
      .flatMap((route) => route.children ?? [])
      .map((child) => child.path)
      .filter((path): path is string => Boolean(path))
  );
  const missingLegalChildren = publicLegalChildren.filter(
    (child) => child.path && !existingLegalPaths.has(child.path)
  );

  if (missingLegalChildren.length === 0) {
    return routes;
  }

  return insertRoutesBeforeAuthFallback(routes, [
    {
      path: '/',
      layout: 'EmptyLayout',
      children: missingLegalChildren
    }
  ]);
}

function removeLegacyPublicProfileChildren(
  routes: AppRouteConfig[]
): AppRouteConfig[] {
  return routes.map((route) => {
    if (route.path !== '/') {
      return route;
    }

    return {
      ...route,
      children: route.children?.filter(
        (child) =>
          child.element !== 'IdCard' &&
          child.element !== 'ClientDynamicPage' &&
          child.path !== 'company/:companyId'
      )
    };
  });
}

function getTenantRoute(path: string) {
  return serviceBusinessTenantRoutes.find((route) => route.path === path);
}

function ensureTenantPublicRoute(route: AppRouteConfig) {
  const localPublicRoute = getTenantRoute('/');

  if (
    !localPublicRoute ||
    !routeHasChildElement(route, serviceBusinessPublicElementNames)
  ) {
    return route;
  }

  return {
    ...route,
    children: mergeRouteChildren(
      route.children,
      localPublicRoute.children ?? []
    )
  };
}

function prepareTenantDomainRoutes(routes: AppRouteConfig[]) {
  const normalizedRoutes = normalizeTenantDomainRouteElements(routes);
  const routesWithoutDeprecatedDashboard = removeBackofficeLayoutConfigRoutes(
    removeDeprecatedDashboardRoutes(removeLegacyDemoRoutes(normalizedRoutes))
  );
  const rootRoute = routesWithoutDeprecatedDashboard.find(
    (route) => route.path === '/'
  );
  const localRootRoute = getTenantRoute('/');
  const preparedRootRoute = rootRoute
    ? ensureTenantPublicRoute(rootRoute)
    : localRootRoute
      ? cloneRoute(localRootRoute)
      : undefined;
  const tenantRequiredRoutes = cloneRoutes(
    serviceBusinessTenantRoutes.filter((route) => route.path !== '/')
  );
  const remainingBackendRoutes = routesWithoutDeprecatedDashboard.filter(
    (route) =>
      route.path !== '/' &&
      !tenantManagedRoutePathNames.has(route.path) &&
      !isTenantBackofficeRoute(route)
  );

  return [
    ...(preparedRootRoute ? [preparedRootRoute] : []),
    ...tenantRequiredRoutes,
    ...remainingBackendRoutes
  ];
}

function inferRouteScope(
  routes: AppRouteConfig[] | null | undefined,
  backendScope?: AppRouteScope,
  tenantId?: string
): AppRouteScope {
  if (backendScope) {
    return backendScope;
  }

  if (tenantId) {
    return 'tenant';
  }

  if (!routes?.length) {
    return 'app';
  }

  if (routesHaveElement(routes, appShellElementNames)) {
    return 'app';
  }

  if (
    routes.some(isTenantBackofficeRoute) ||
    routesHaveElement(routes, tenantPublicElementNames)
  ) {
    return 'tenant';
  }

  return 'app';
}

function createRouteContext(
  scope: AppRouteScope,
  tenantId?: string,
  publicProfileFriendlyUrl?: string
): AppRouteContext {
  return {
    scope,
    tenantId,
    publicProfileFriendlyUrl
  };
}

export async function fetchRoutesFromApi(): Promise<AppRouteConfig[]> {
  const host = normalizeHost(window.location.hostname);
  const [tenantResolution, routesFromBackend] = await Promise.all([
    resolveTenantContext(host),
    fetchRoutesFromBackend(host)
  ]);
  const usesSharedApplicationHost =
    isLocalHost(host) || isApplicationHost(host);
  const tenantId =
    tenantResolution?.tenantId ??
    (usesSharedApplicationHost ? undefined : routesFromBackend?.tenantId);
  const publicProfileFriendlyUrl =
    tenantResolution?.publicProfileFriendlyUrl ??
    (usesSharedApplicationHost
      ? undefined
      : routesFromBackend?.publicProfileFriendlyUrl);
  const scope = inferRouteScope(
    routesFromBackend?.routes,
    usesSharedApplicationHost ? 'app' : routesFromBackend?.scope,
    tenantId
  );

  if (tenantId) {
    setStoredTenantId(tenantId);
  }

  setRouteContext(
    createRouteContext(scope, tenantId, publicProfileFriendlyUrl)
  );

  if (routesFromBackend) {
    return scope === 'tenant'
      ? prepareTenantDomainRoutes(routesFromBackend.routes)
      : prepareAppRoutesFromBackend(routesFromBackend.routes);
  }

  return scope === 'tenant'
    ? cloneRoutes(serviceBusinessTenantRoutes)
    : [
        ...cloneRoutes(defaultAppRoutePreset.routes),
        ...(defaultAppRoutePreset.includeSharedRoutes
          ? cloneRoutes(sharedRoutes)
          : [])
      ];
}
